import type { TurnWorld, Unit, Domain } from './types';
import { UNIT_DEFS } from './units';

// 8-directional (Chebyshev) neighbourhood — ZoC, like attack range, extends
// diagonally as well as orthogonally.
const DIRECTIONS: readonly [number, number][] = [
  [0, -1], [0, 1], [-1, 0], [1, 0],
  [-1, -1], [1, -1], [-1, 1], [1, 1],
];

/**
 * Check whether an enemy unit's ZoC affects a unit of the given domain.
 * Land → land, air → air, water → water.
 * Special: aa-flakpanzer (land) projects ZoC against both land and air.
 */
function affectsDomain(enemyType: string, enemyDomain: Domain, targetDomain: Domain): boolean {
  if (enemyType === 'aa-flakpanzer') {
    return targetDomain === 'land' || targetDomain === 'air';
  }
  return enemyDomain === targetDomain;
}

/**
 * Compute the set of tile keys ("col,row") that are under enemy Zone of Control
 * for the given moving unit.
 *
 * Rules:
 * - Only visible enemies with attack > 0 generate ZoC.
 * - Domain-aware: enemy ZoC only affects units in matching domains.
 * - ZoC = 8 neighbours (orthogonal + diagonal) of each qualifying enemy.
 * - Enemy's own tile is NOT included (already blocked by occupied-tile logic).
 */
export function computeZocTiles(
  world: TurnWorld,
  movingUnit: Unit,
  visible: Set<string>,
): Set<string> {
  const zoc = new Set<string>();
  const movingDomain = UNIT_DEFS[movingUnit.type].domain;

  for (const [, enemy] of world.units) {
    if (enemy.owner === movingUnit.owner) continue;

    const enemyKey = `${enemy.col},${enemy.row}`;
    if (!visible.has(enemyKey)) continue;

    const enemyDef = UNIT_DEFS[enemy.type];
    if (enemyDef.attack <= 0) continue;

    if (!affectsDomain(enemy.type, enemyDef.domain, movingDomain)) continue;

    for (const [dc, dr] of DIRECTIONS) {
      const nc = enemy.col + dc;
      const nr = enemy.row + dr;
      if (nc < 0 || nr < 0 || nc >= world.map.width || nr >= world.map.height) continue;
      zoc.add(`${nc},${nr}`);
    }
  }

  return zoc;
}
